import React, { useEffect, useRef, useState } from "react";
import { Megaphone } from "lucide-react";
import { useAnalytics } from "../../utils/Customer/useAnalytics";

interface StripBannerProps {
  imageUrl?: string;
  alt?: string;
  isSponsored?: boolean;
  sponsoredText?: string;

  /**
   * Banner visual size.
   *
   * small  = 1380 × 75
   * medium = 1380 × 90
   * large  = 1380 × 120
   */
  size?: "small" | "medium" | "large";

  /**
   * Analytics banner ID.
   */
  bannerId?: number;

  /**
   * Called when the banner is clicked.
   */
  onClick?: () => void;
}

const StripBanner: React.FC<StripBannerProps> = ({
  imageUrl = "/images/pet-strip-banner.webp",
  alt = "Promotional Banner",
  isSponsored = false,
  sponsoredText = "Sponsored",
  size = "large",
  bannerId,
  onClick,
}) => {
  const { trackBannerView, trackBannerClick } = useAnalytics();

  const bannerRef = useRef<HTMLDivElement | null>(null);

  const [isVisible, setIsVisible] = useState(false);

  /*
   * ---------------------------------------------------------
   * Backend banner dimensions
   * ---------------------------------------------------------
   */
  const aspectRatio =
    size === "small"
      ? "aspect-[1380/75]"
      : size === "medium"
        ? "aspect-[1380/90]"
        : "aspect-[1380/120]";

  /*
   * ---------------------------------------------------------
   * Responsive spacing
   * ---------------------------------------------------------
   */
  const wrapperSpacing =
    size === "small" ? "py-1" : size === "medium" ? "py-1.5" : "py-2";

  /*
   * ---------------------------------------------------------
   * Analytics: Visibility
   *
   * Track the banner only when at least 50% of the banner
   * enters the viewport.
   *
   * Duplicate prevention is handled centrally by
   * useAnalytics().
   * ---------------------------------------------------------
   */
  useEffect(() => {
    const element = bannerRef.current;

    if (!element || !bannerId) {
      console.log("StripBanner analytics skipped:", {
        hasElement: !!element,
        bannerId,
      });
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        console.log("StripBanner visibility:", {
          bannerId,
          isIntersecting: entry.isIntersecting,
          ratio: entry.intersectionRatio,
        });

        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [bannerId]);

  /*
   * ---------------------------------------------------------
   * Analytics: Banner View
   * ---------------------------------------------------------
   */
  useEffect(() => {
    console.log("StripBanner tracking check:", {
      bannerId,
      isVisible,
    });

    if (!isVisible || !bannerId) {
      return;
    }

    console.log("TRACKING STRIP BANNER VIEW:", bannerId);

    trackBannerView(bannerId);
  }, [isVisible, bannerId, trackBannerView]);

  /*
   * ---------------------------------------------------------
   * Banner Click
   * ---------------------------------------------------------
   */
  const handleClick = () => {
    if (bannerId) {
      trackBannerClick(bannerId);
    }

    onClick?.();
  };

  /*
   * ---------------------------------------------------------
   * Keyboard interaction
   * ---------------------------------------------------------
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div ref={bannerRef} className={`w-full ${wrapperSpacing}`}>
      <div
        onClick={handleClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={handleKeyDown}
        className="
          group
          relative
          w-full
          overflow-hidden
          rounded-xl
          sm:rounded-2xl
          bg-slate-100
          cursor-pointer
          shadow-[0_2px_12px_rgba(15,23,42,0.06)]
          hover:shadow-[0_6px_22px_rgba(15,23,42,0.10)]
          transition-all
          duration-300
        "
      >
        {/* -------------------------------------------------
            Banner image
        -------------------------------------------------- */}

        <div className={`relative w-full ${aspectRatio}`}>
          <img
            src={imageUrl}
            alt={alt}
            loading="lazy"
            draggable={false}
            className="
              absolute
              inset-0
              block
              h-full
              w-full
              object-cover
              transition-transform
              duration-500
              ease-out
              group-hover:scale-[1.008]
            "
          />

          {/* -------------------------------------------------
              Very subtle image overlay
          -------------------------------------------------- */}

          {isSponsored && (
            <div
              className="
                pointer-events-none
                absolute
                inset-x-0
                top-0
                h-12
                bg-gradient-to-b
                from-black/10
                to-transparent
              "
            />
          )}

          {/* -------------------------------------------------
              Sponsored badge
          -------------------------------------------------- */}

          {isSponsored && (
            <div
              className="
                absolute
                right-2
                top-2
                z-10
                sm:right-3
                sm:top-3
              "
            >
              <div
                className={`
                  flex
                  items-center
                  gap-1.5
                  rounded-full
                  border
                  border-white/30
                  bg-black/75
                  px-2.5
                  py-1
                  sm:px-3
                  sm:py-1.5
                  text-white
                  shadow-lg
                  backdrop-blur-md
                  ${
                    size === "small"
                      ? "text-[9px] sm:text-[10px]"
                      : "text-[10px] sm:text-[11px]"
                  }
                `}
              >
                <Megaphone
                  size={size === "small" ? 10 : 11}
                  strokeWidth={2.5}
                />

                <span
                  className="
                    whitespace-nowrap
                    font-bold
                    tracking-wide
                  "
                >
                  {sponsoredText}
                </span>
              </div>
            </div>
          )}

          {/* -------------------------------------------------
              Hover interaction
          -------------------------------------------------- */}

          <div
            className="
              pointer-events-none
              absolute
              inset-0
              bg-black/0
              transition-colors
              duration-300
              group-hover:bg-black/[0.025]
            "
          />
        </div>
      </div>
    </div>
  );
};

export default StripBanner;
